import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Loader2, Mail } from "lucide-react";
import { adminLogin, isAdminAuthed } from "@/lib/blogApi";

const AdminLogin = () => {
  const [email, setEmail] = useState("admin@spiruva.com");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const navigate = useNavigate();

  if (isAdminAuthed()) return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      await adminLogin(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Incorrect email or password.";
      setError(msg);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="pt-[72px] min-h-screen flex items-center" data-testid="page-admin-login">
      <div className="container-narrow py-20 w-full">
        <div className={`max-w-md mx-auto bg-white border border-brand-green/15 p-10 ${shake ? "animate-shake" : ""}`}>
          <div className="w-12 h-12 bg-brand-green text-brand-cream flex items-center justify-center mb-6">
            <Lock size={18} />
          </div>
          <p className="eyebrow mb-2">◦  Spiruva Admin</p>
          <h1 className="font-display text-3xl text-brand-black">Sign in to continue.</h1>
          <p className="text-sm text-brand-black/60 mt-3">Server-side authentication · JWT-protected session.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" autoComplete="on">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/55 flex items-center gap-1.5">
                <Mail size={11} /> Email
              </span>
              <input
                type="email"
                required
                autoComplete="username"
                data-testid="admin-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1.5 px-4 py-3 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none text-sm"
              />
            </div>
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/55 flex items-center gap-1.5">
                <Lock size={11} /> Password
              </span>
              <input
                type="password"
                required
                autoFocus
                autoComplete="current-password"
                data-testid="admin-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-1.5 px-4 py-3 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none text-sm"
              />
            </div>
            {error && <p className="text-sm text-red-700" data-testid="admin-error">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-submit"
              className="w-full btn-primary disabled:opacity-60"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Verifying</> : "Sign in"}
            </button>
          </form>

          <p className="text-[11px] text-brand-black/45 mt-8 font-mono leading-relaxed">
            Restricted access. Sessions expire after 12 hours.
            <br />Trouble signing in? Contact <span className="text-brand-teal">export@spiruva.com</span>.
          </p>
        </div>
      </div>

      <style>{`@keyframes shake {0%,100%{transform:translateX(0)}25%{transform:translateX(-6px)}75%{transform:translateX(6px)}} .animate-shake{animation:shake 0.4s}`}</style>
    </div>
  );
};

export default AdminLogin;
